import { Button } from '@ophelos/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ophelos/ui/components/dropdown-menu';
import { ArrowLeftRight } from 'lucide-react';
import { CountryFlag, InternationalFlag } from '@/components/icons/FlagIcons';
import { type MarketHub, type MarketHubs, marketLoginUrl, marketShortCode } from '@/lib/market-hubs';

/** The international market has no country of its own, so it gets the globe mark instead of its headquarters flag. */
export function MarketFlag({ market, className = 'size-4 rounded-sm' }: { market: MarketHub; className?: string }) {
  if (market.code.endsWith('_INTL')) return <InternationalFlag className={className} />;
  return <CountryFlag country={market.country} className={className} />;
}

/**
 * WorkOS sends every sign-in back to one Hub, so a user invited to another market lands on the wrong
 * Hub, either on its login page or, after an invite, already authenticated and refused by
 * MarketOrganizationGuard. Offer every sibling market Hub so they can move; the target Hub pins its own
 * org on sign-in, so no sign-out is needed first.
 */
export function MarketSwitcher({ markets, className }: { markets: MarketHubs; className?: string }) {
  if (markets.others.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={className}>
          <ArrowLeftRight className="size-4" />
          Change market
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="max-h-80 w-64 overflow-y-auto">
        <DropdownMenuLabel>Log in to another market</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {markets.others.map((market) => (
          <DropdownMenuItem key={market.code} onSelect={() => window.location.assign(marketLoginUrl(market))}>
            <MarketFlag market={market} />
            <span className="flex-1">{market.name}</span>
            <span className="text-xs text-muted-foreground">{marketShortCode(market)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
